import { CommonModule, CurrencyPipe } from '@angular/common';
import { Component, OnInit, computed, effect, inject, signal } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { BackofficeCompany } from './backoffice.models';
import { BackofficeService } from './backoffice.service';

function trimmedRequiredValidator(control: AbstractControl): ValidationErrors | null {
  return String(control.value ?? '').trim() ? null : { required: true };
}

@Component({
  selector: 'app-backoffice-companies-page',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, CurrencyPipe],
  templateUrl: './companies-page.component.html',
  styleUrl: './companies-page.component.css'
})
export class BackofficeCompaniesPageComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly backofficeService = inject(BackofficeService);

  readonly overview = this.backofficeService.overview;
  readonly companies = computed(() =>
    [...this.backofficeService.companies()].sort((a, b) => Number(b.monthlyTotalEuros || 0) - Number(a.monthlyTotalEuros || 0))
  );
  readonly plans = this.backofficeService.planOptions;
  readonly canManageCompanies = this.backofficeService.userCanManageCompanies;

  readonly selectedCompanyId = signal<string | null>(null);
  readonly saving = signal(false);
  readonly actionCompanyId = signal<string | null>(null);
  readonly feedback = signal<string | null>(null);
  readonly feedbackType = signal<'success' | 'error'>('success');

  readonly editablePlans = computed(() => {
    const selectedCompany = this.selectedCompanyId()
      ? this.companies().find((company) => company.id === this.selectedCompanyId())
      : null;
    const forcedVisiblePlanKeys = new Set(selectedCompany?.selectedPlanKeys || []);
    Object.entries(selectedCompany?.seatQuotaByPlan || {}).forEach(([planKey, quota]) => {
      if (Number(quota || 0) > 0) {
        forcedVisiblePlanKeys.add(planKey);
      }
    });

    return this.plans().filter((plan) => plan.active || forcedVisiblePlanKeys.has(plan.key));
  });

  readonly totalSeatCount = computed(() =>
    this.companies().reduce(
      (sum, company) =>
        sum +
        this.plans().reduce(
          (planSum, plan) => planSum + Number(company.seatQuotaByPlan[plan.key] || 0),
          0
        ),
      0
    )
  );

  readonly usedSeatCount = computed(() =>
    this.companies().reduce(
      (sum, company) =>
        sum +
        this.plans().reduce(
          (planSum, plan) => planSum + Number(company.usedSeatsByPlan[plan.key] || 0),
          0
        ),
      0
    )
  );

  readonly form = this.fb.group({
    id: this.fb.nonNullable.control(''),
    name: this.fb.nonNullable.control('', [Validators.required, trimmedRequiredValidator]),
    selectedPlanKeys: this.fb.group({}),
    seatQuotaByPlan: this.fb.group({}),
    contactFirstName: this.fb.nonNullable.control(''),
    contactLastName: this.fb.nonNullable.control(''),
    contactEmail: this.fb.nonNullable.control(''),
    contactPhone: this.fb.nonNullable.control(''),
    contactJobTitle: this.fb.nonNullable.control(''),
    contactUsername: this.fb.nonNullable.control('')
  });

  constructor() {
    effect(() => {
      this.syncPlanControls(this.plans().map((plan) => plan.key));
    });
  }

  ngOnInit(): void {
    this.backofficeService.loadOverview();
  }

  startCreate({ preserveFeedback = false }: { preserveFeedback?: boolean } = {}): void {
    this.selectedCompanyId.set(null);
    if (!preserveFeedback) {
      this.feedback.set(null);
    }
    this.form.patchValue({
      id: '',
      name: '',
      contactFirstName: '',
      contactLastName: '',
      contactEmail: '',
      contactPhone: '',
      contactJobTitle: '',
      contactUsername: ''
    }, { emitEvent: false });
    this.resetPlanControls();
  }

  editCompany(company: BackofficeCompany): void {
    this.selectedCompanyId.set(company.id);
    this.feedback.set(null);
    this.form.patchValue({
      id: company.id,
      name: company.name,
      contactFirstName: company.contact.firstName || '',
      contactLastName: company.contact.lastName || '',
      contactEmail: company.contact.email || '',
      contactPhone: company.contact.phone || '',
      contactJobTitle: company.contact.jobTitle || '',
      contactUsername: company.contact.username || ''
    }, { emitEvent: false });

    const selectedPlanControls = Object.fromEntries(
      this.plans().map((plan) => [plan.key, company.selectedPlanKeys.includes(plan.key)])
    );
    const seatQuotaControls = Object.fromEntries(
      this.plans().map((plan) => [plan.key, Number(company.seatQuotaByPlan[plan.key] || 0)])
    );

    this.selectedPlanGroup.patchValue(selectedPlanControls, { emitEvent: false });
    this.seatQuotaGroup.patchValue(seatQuotaControls, { emitEvent: false });
  }

  submit(): void {
    if (!this.canManageCompanies() || this.form.invalid || this.saving()) {
      this.form.markAllAsTouched();
      return;
    }

    const rawValue = this.form.getRawValue();
    const rawSeatQuotaByPlan = (rawValue.seatQuotaByPlan || {}) as Record<string, number>;
    const normalizedName = rawValue.name.trim();
    const normalizedSeatQuotaByPlan = Object.fromEntries(
      this.plans().map((plan) => [plan.key, Math.max(0, Number(rawSeatQuotaByPlan[plan.key] || 0))])
    );
    const explicitlySelectedPlanKeys = Object.entries(rawValue.selectedPlanKeys || {})
      .filter(([, selected]) => Boolean(selected))
      .map(([planKey]) => planKey);
    const selectedPlanKeys = this.plans()
      .map((plan) => plan.key)
      .filter((planKey) => explicitlySelectedPlanKeys.includes(planKey) || Number(normalizedSeatQuotaByPlan[planKey] || 0) > 0);

    if (!normalizedName) {
      this.feedbackType.set('error');
      this.feedback.set('Le nom de la compagnie est obligatoire.');
      return;
    }

    if (selectedPlanKeys.length === 0) {
      this.feedbackType.set('error');
      this.feedback.set('Selectionnez au moins un plan ou renseignez au moins un quota positif.');
      return;
    }

    this.saving.set(true);
    this.feedback.set(null);

    this.backofficeService.saveCompany({
      id: rawValue.id || undefined,
      name: normalizedName,
      selectedPlanKeys,
      seatQuotaByPlan: normalizedSeatQuotaByPlan,
      contact: {
        firstName: rawValue.contactFirstName.trim(),
        lastName: rawValue.contactLastName.trim(),
        email: rawValue.contactEmail.trim(),
        phone: rawValue.contactPhone.trim(),
        jobTitle: rawValue.contactJobTitle.trim(),
        username: rawValue.contactUsername.trim()
      }
    }).subscribe({
      next: () => {
        this.saving.set(false);
        this.feedbackType.set('success');
        this.feedback.set(rawValue.id ? 'Compagnie mise a jour.' : 'Compagnie creee.');
        this.startCreate({ preserveFeedback: true });
      },
      error: (error) => {
        this.saving.set(false);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error, error?.error?.details));
      }
    });
  }

  deleteCompany(company: BackofficeCompany): void {
    const confirmed = window.confirm(`Supprimer la compagnie ${company.name} ?`);
    if (!confirmed) {
      return;
    }

    this.actionCompanyId.set(company.id);
    this.feedback.set(null);

    this.backofficeService.deleteCompany(company.id).subscribe({
      next: () => {
        this.actionCompanyId.set(null);
        this.feedbackType.set('success');
        this.feedback.set('Compagnie supprimee.');
      },
      error: (error) => {
        this.actionCompanyId.set(null);
        this.feedbackType.set('error');
        this.feedback.set(this.formatError(error?.error?.error));
      }
    });
  }

  trackCompany(_: number, company: BackofficeCompany): string {
    return company.id;
  }

  trackPlan(_: number, plan: { key: string }): string {
    return plan.key;
  }

  isPlanInactive(planKey: string): boolean {
    return !this.plans().find((plan) => plan.key === planKey)?.active;
  }

  get selectedPlanGroup(): FormGroup {
    return this.form.get('selectedPlanKeys') as FormGroup;
  }

  get seatQuotaGroup(): FormGroup {
    return this.form.get('seatQuotaByPlan') as FormGroup;
  }

  planSelectionControl(planKey: string): FormControl<boolean> {
    const existingControl = this.selectedPlanGroup.get(planKey);
    if (existingControl instanceof FormControl) {
      return existingControl as FormControl<boolean>;
    }

    const control = new FormControl(false, { nonNullable: true });
    this.selectedPlanGroup.addControl(planKey, control);
    return control;
  }

  seatQuotaControl(planKey: string): FormControl<number> {
    const existingControl = this.seatQuotaGroup.get(planKey);
    if (existingControl instanceof FormControl) {
      return existingControl as FormControl<number>;
    }

    const control = new FormControl(0, { nonNullable: true });
    this.seatQuotaGroup.addControl(planKey, control);
    return control;
  }

  private syncPlanControls(planKeys: string[]): void {
    this.syncControlGroup(this.selectedPlanGroup, planKeys, false);
    this.syncControlGroup(this.seatQuotaGroup, planKeys, 0);
  }

  private syncControlGroup(group: FormGroup, planKeys: string[], defaultValue: boolean | number): void {
    const existingPlanKeys = Object.keys(group.controls);

    for (const planKey of planKeys) {
      if (!group.contains(planKey)) {
        group.addControl(planKey, new FormControl(defaultValue, { nonNullable: true }));
      }
    }

    for (const planKey of existingPlanKeys) {
      if (!planKeys.includes(planKey)) {
        group.removeControl(planKey);
      }
    }
  }

  private resetPlanControls(): void {
    this.selectedPlanGroup.patchValue(
      Object.fromEntries(this.plans().map((plan) => [plan.key, false])),
      { emitEvent: false }
    );
    this.seatQuotaGroup.patchValue(
      Object.fromEntries(this.plans().map((plan) => [plan.key, 0])),
      { emitEvent: false }
    );
  }

  private formatError(code: string | undefined, details?: { duplicateCompanyName?: string | null }): string {
    switch (code) {
      case 'AT_LEAST_ONE_PLAN_REQUIRED':
        return 'Selectionnez au moins un plan.';
      case 'COMPANY_ALREADY_EXISTS':
        return details?.duplicateCompanyName
          ? `La compagnie "${details.duplicateCompanyName}" existe deja.`
          : 'Cette compagnie existe deja.';
      case 'COMPANY_NAME_REQUIRED':
        return 'Le nom de la compagnie est obligatoire.';
      case 'SUPERADMIN_REQUIRED':
        return 'Cette action est reservee au superadmin.';
      default:
        return code ? `L action sur la compagnie a echoue (${code}).` : 'L action sur la compagnie a echoue.';
    }
  }
}
